import React, { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const ServiceTestimonials = ({ testimonials, serviceName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAnimating) {
        setIsAnimating(true);
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
        setTimeout(() => setIsAnimating(false), 500);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isAnimating, testimonials.length]);

  const handleNav = (dir) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => dir === 'next' ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const getVisible = () => {
    const prev = (currentIndex - 1 + testimonials.length) % testimonials.length;
    const next = (currentIndex + 1) % testimonials.length;
    return [testimonials[prev], testimonials[currentIndex], testimonials[next]];
  };

  const visible = getVisible();

  return (
    <section className="py-20 bg-cream-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-wine-500 mb-3">Client Reviews</p>
          <h2 className="text-3xl font-bold text-wine-950 mb-3">What Our Clients Say</h2>
          <p className="text-stone-500">Real results from clients who used our {serviceName} services.</p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-5">
            {visible.map((t, i) => (
              <div key={`${i}`}
                className={`bg-white rounded-xl p-7 border transition-all duration-500 ${i === 1 ? 'border-gold-400 shadow-lg shadow-gold-500/10' : 'border-stone-200 opacity-60 hidden md:block'}`}>
                <Quote className="w-7 h-7 text-stone-300 mb-3" />
                <p className="text-stone-600 text-sm leading-relaxed mb-5 min-h-[70px]">"{t.text}"</p>
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (<Star key={j} className="w-4 h-4 fill-gold-400 text-gold-400" />))}
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-wine-100 text-wine-600 flex items-center justify-center font-bold text-sm border border-wine-200">{t.name.charAt(0)}</div>
                  <div>
                    <div className="font-semibold text-wine-950 text-sm">{t.name}</div>
                    <div className="text-xs text-stone-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-3 mt-8">
            <Button onClick={() => handleNav('prev')} disabled={isAnimating} variant="outline" className="rounded-full w-9 h-9 p-0 border-stone-300 bg-white hover:bg-stone-50 text-stone-600">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-1.5">
              {testimonials.map((_, index) => (
                <button key={index} onClick={() => { if (!isAnimating) { setIsAnimating(true); setCurrentIndex(index); setTimeout(() => setIsAnimating(false), 500); } }} className={`rounded-full transition-all ${index === currentIndex ? 'w-5 h-1.5 bg-wine-600' : 'w-1.5 h-1.5 bg-stone-300'}`} />
              ))}
            </div>
            <Button onClick={() => handleNav('next')} disabled={isAnimating} variant="outline" className="rounded-full w-9 h-9 p-0 border-stone-300 bg-white hover:bg-stone-50 text-stone-600">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceTestimonials;
